import { useState } from 'react';
import { LandingPageThemeId } from '@client/new/common/landingPageThemes';
import { resolvedVisualVersion } from '@client/new/common/landingPageVisualVersion';
import { makeAsync } from '@client/new/helpers/async';
import { Async } from '@client/new/helpers/types';
import {
  useSetLandingPageLookMutation,
  useSetMyLandingPageLookMutation,
} from '../../../../../generated/graphql';
import {
  PageLook,
  PageLookEditor,
  themeOrDefault,
  themeToSave,
} from './pageLook';

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const usePageLook = (
  initialLook: PageLook,
  requested: LandingPageThemeId | null,
  editor: PageLookEditor | null,
) => {
  const initialVersion = resolvedVisualVersion(initialLook.visualVersion);
  const [look, setLook] = useState<PageLook>({
    ...initialLook,
    visualVersion: initialVersion,
  });
  const [selectedTheme, setSelectedTheme] = useState<LandingPageThemeId>(
    requested ?? themeOrDefault(initialLook.savedTheme),
  );
  const [isSwitchOpen, setIsSwitchOpen] = useState(
    requested !== null && initialVersion === 1,
  );
  const [switchStatus, setSwitchStatus] = useState<Async<null>>(
    makeAsync.notFetched(),
  );
  const [setMyLandingPageLook] = useSetMyLandingPageLookMutation();
  const [setLandingPageLook] = useSetLandingPageLookMutation();

  const writeLook = async (theme: LandingPageThemeId) => {
    if (editor?.role === 'staff') {
      await setLandingPageLook({
        variables: { influencerId: editor.influencerId, theme },
      });
    } else {
      await setMyLandingPageLook({ variables: { theme } });
    }
  };

  const pendingTheme = themeToSave(look, selectedTheme);

  const confirmSwitch = async () => {
    setSwitchStatus(makeAsync.fetching());
    try {
      await writeLook(selectedTheme);
      setLook({ visualVersion: 2, savedTheme: selectedTheme });
      setSwitchStatus(makeAsync.fetched(null));
      setIsSwitchOpen(false);
    } catch (error) {
      setSwitchStatus(makeAsync.error(errorMessage(error)));
    }
  };

  const savePendingTheme = async () => {
    if (!pendingTheme) return;
    await writeLook(pendingTheme);
    setLook((current) => ({ ...current, savedTheme: pendingTheme }));
  };

  return {
    visualVersion: look.visualVersion,
    selectedTheme,
    selectTheme: setSelectedTheme,
    isThemeDirty: pendingTheme !== null,
    savePendingTheme,
    isSwitchOpen,
    openSwitch: () => {
      setSwitchStatus(makeAsync.notFetched());
      setIsSwitchOpen(true);
    },
    closeSwitch: () => setIsSwitchOpen(false),
    confirmSwitch,
    switchStatus,
    justSwitched: switchStatus.type === 'fetched',
  };
};
